import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    ArrowLeft,
    FileSpreadsheet,
    Download,
    Plus,
    Trash2,
    GripVertical,
    Save,
    Eye,
    FileText,
    Globe,
    Calculator,
    Filter,
    ArrowUpDown,
    Loader2,
    Database,
    Settings2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportesCustomPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);
    
    // Fuentes y campos
    const [fuentes, setFuentes] = useState([]);
    const [campos, setCampos] = useState([]);
    const [fuenteActual, setFuenteActual] = useState('');
    
    // Configuración del reporte
    const [nombreReporte, setNombreReporte] = useState('Mi Reporte');
    const [columnasSeleccionadas, setColumnasSeleccionadas] = useState([]);
    const [filtros, setFiltros] = useState([]);
    const [calculos, setCalculos] = useState([]);
    const [ordenarPor, setOrdenarPor] = useState('');
    const [orden, setOrden] = useState('asc');
    
    // Resultado
    const [resultado, setResultado] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    
    // Plantillas
    const [plantillas, setPlantillas] = useState([]);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [nombrePlantilla, setNombrePlantilla] = useState('');

    useEffect(() => {
        loadFuentes();
        loadPlantillas();
    }, []);

    useEffect(() => {
        if (fuenteActual) {
            loadCampos(fuenteActual);
        }
    }, [fuenteActual]);

    const getToken = () => localStorage.getItem('token');

    const loadFuentes = async () => {
        try {
            const res = await fetch(`${API_URL}/api/reportes/fuentes`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            const data = await res.json();
            setFuentes(data.fuentes || []);
            if (data.fuentes?.length > 0) {
                setFuenteActual(data.fuentes[0].codigo);
            }
        } catch (err) {
            toast.error('Error al cargar fuentes de datos');
        } finally {
            setLoading(false);
        }
    };

    const loadCampos = async (fuente) => {
        try {
            const res = await fetch(`${API_URL}/api/reportes/campos/${fuente}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            const data = await res.json();
            setCampos(data.campos || []);
            // Seleccionar todas las columnas por defecto
            setColumnasSeleccionadas(data.campos?.map((c, i) => ({
                field: c.field,
                label: c.label,
                visible: true,
                order: i,
                format: c.tipo === 'currency' ? 'currency' : c.tipo === 'date' ? 'date' : null
            })) || []);
        } catch (err) {
            toast.error('Error al cargar campos');
        }
    };

    const loadPlantillas = async () => {
        try {
            const res = await fetch(`${API_URL}/api/reportes/plantillas`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            const data = await res.json();
            setPlantillas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error loading plantillas:', err);
        }
    };

    const toggleColumna = (field) => {
        setColumnasSeleccionadas(prev => 
            prev.map(c => c.field === field ? { ...c, visible: !c.visible } : c)
        );
    };

    const moveColumna = (field, direction) => {
        setColumnasSeleccionadas(prev => {
            const idx = prev.findIndex(c => c.field === field);
            if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === prev.length - 1)) {
                return prev;
            }
            const newIdx = direction === 'up' ? idx - 1 : idx + 1;
            const newCols = [...prev];
            [newCols[idx], newCols[newIdx]] = [newCols[newIdx], newCols[idx]];
            return newCols.map((c, i) => ({ ...c, order: i }));
        });
    };

    const addFiltro = () => {
        if (campos.length === 0) return;
        setFiltros(prev => [...prev, {
            campo: campos[0].field,
            operador: 'eq',
            valor: ''
        }]);
    };

    const updateFiltro = (index, field, value) => {
        setFiltros(prev => prev.map((f, i) => 
            i === index ? { ...f, [field]: value } : f
        ));
    };

    const removeFiltro = (index) => {
        setFiltros(prev => prev.filter((_, i) => i !== index));
    };

    const addCalculo = () => {
        setCalculos(prev => [...prev, {
            nombre: 'Total',
            tipo: 'suma',
            campo: campos[0]?.field || '',
            formula: '',
            formato: null
        }]);
    };

    const updateCalculo = (index, field, value) => {
        setCalculos(prev => prev.map((c, i) => 
            i === index ? { ...c, [field]: value } : c
        ));
    };

    const removeCalculo = (index) => {
        setCalculos(prev => prev.filter((_, i) => i !== index));
    };

    const generarReporte = async () => {
        setGenerating(true);
        try {
            const config = {
                nombre: nombreReporte,
                fuente: fuenteActual,
                columnas: columnasSeleccionadas,
                filtros: filtros.filter(f => f.valor !== ''),
                calculos,
                ordenar_por: ordenarPor || null,
                orden
            };

            const res = await fetch(`${API_URL}/api/reportes/generar`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(config)
            });

            if (!res.ok) throw new Error('Error al generar reporte');
            
            const data = await res.json();
            setResultado(data);
            setPreviewData(data.datos || []);
            toast.success(`Reporte generado: ${data.total_registros} registros`);
        } catch (err) {
            toast.error('Error al generar el reporte');
        } finally {
            setGenerating(false);
        }
    };

    const exportarReporte = async (formato) => {
        setExporting(true);
        try {
            const config = {
                nombre: nombreReporte,
                fuente: fuenteActual,
                columnas: columnasSeleccionadas,
                filtros: filtros.filter(f => f.valor !== ''),
                calculos,
                ordenar_por: ordenarPor || null,
                orden
            };

            const res = await fetch(`${API_URL}/api/reportes/exportar/${formato}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(config)
            });

            if (!res.ok) throw new Error('Error al exportar');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            const extensions = { csv: 'csv', excel: 'xlsx', pdf: 'pdf', html: 'html', kmz: 'kmz' };
            a.download = `${nombreReporte.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${extensions[formato]}`;
            
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success(`Exportado como ${formato.toUpperCase()}`);
        } catch (err) {
            toast.error('Error al exportar');
        } finally {
            setExporting(false);
        }
    };

    const guardarPlantilla = async () => {
        if (!nombrePlantilla.trim()) {
            toast.error('Ingresa un nombre para la plantilla');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/reportes/plantillas`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    nombre: nombrePlantilla,
                    config: {
                        nombre: nombreReporte,
                        fuente: fuenteActual,
                        columnas: columnasSeleccionadas,
                        filtros,
                        calculos,
                        ordenar_por: ordenarPor,
                        orden
                    }
                })
            });

            if (!res.ok) throw new Error('Error al guardar');
            
            toast.success('Plantilla guardada');
            setShowSaveDialog(false);
            setNombrePlantilla('');
            loadPlantillas();
        } catch (err) {
            toast.error('Error al guardar plantilla');
        }
    };

    const cargarPlantilla = (plantilla) => {
        const config = plantilla.config;
        setNombreReporte(config.nombre || 'Mi Reporte');
        setFuenteActual(config.fuente);
        setColumnasSeleccionadas(config.columnas || []);
        setFiltros(config.filtros || []);
        setCalculos(config.calculos || []);
        setOrdenarPor(config.ordenar_por || '');
        setOrden(config.orden || 'asc');
        toast.success(`Plantilla "${plantilla.nombre}" cargada`);
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid md:grid-cols-3 gap-6">
                    <Skeleton className="h-96" />
                    <Skeleton className="h-96 md:col-span-2" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/reportes')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Settings2 className="h-6 w-6" />
                            Constructor de Reportes
                        </h1>
                        <p className="text-muted-foreground">Crea reportes personalizados con las columnas y filtros que necesites</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowSaveDialog(true)} data-testid="save-template-btn">
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Plantilla
                    </Button>
                    <Button onClick={generarReporte} disabled={generating} data-testid="generate-btn">
                        {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                        Vista Previa
                    </Button>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                {/* Panel de Configuración */}
                <div className="space-y-4">
                    {/* Nombre y Fuente */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Database className="h-4 w-4" />
                                Configuración
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nombre del Reporte</Label>
                                <Input
                                    value={nombreReporte}
                                    onChange={(e) => setNombreReporte(e.target.value)}
                                    placeholder="Mi Reporte"
                                    data-testid="report-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fuente de Datos</Label>
                                <Select value={fuenteActual} onValueChange={setFuenteActual}>
                                    <SelectTrigger data-testid="source-select">
                                        <SelectValue placeholder="Selecciona fuente" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {fuentes.map(f => (
                                            <SelectItem key={f.codigo} value={f.codigo}>
                                                {f.nombre} ({f.campos} campos)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Columnas */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <FileSpreadsheet className="h-4 w-4" />
                                Columnas
                            </CardTitle>
                            <CardDescription>Selecciona y ordena las columnas</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {columnasSeleccionadas.map((col, idx) => (
                                    <div key={col.field} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                                        <Checkbox
                                            checked={col.visible}
                                            onCheckedChange={() => toggleColumna(col.field)}
                                            data-testid={`col-${col.field}`}
                                        />
                                        <span className="flex-1 text-sm truncate">{col.label}</span>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => moveColumna(col.field, 'up')}
                                                disabled={idx === 0}
                                            >
                                                <ArrowUpDown className="h-3 w-3 rotate-180" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => moveColumna(col.field, 'down')}
                                                disabled={idx === columnasSeleccionadas.length - 1}
                                            >
                                                <ArrowUpDown className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Filtros */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Filter className="h-4 w-4" />
                                    Filtros
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={addFiltro}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {filtros.map((filtro, idx) => (
                                    <div key={idx} className="flex gap-2 items-end">
                                        <div className="flex-1">
                                            <Select 
                                                value={filtro.campo} 
                                                onValueChange={(v) => updateFiltro(idx, 'campo', v)}
                                            >
                                                <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {campos.map(c => (
                                                        <SelectItem key={c.field} value={c.field}>{c.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Select 
                                            value={filtro.operador} 
                                            onValueChange={(v) => updateFiltro(idx, 'operador', v)}
                                        >
                                            <SelectTrigger className="w-20 h-8 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="eq">=</SelectItem>
                                                <SelectItem value="neq">≠</SelectItem>
                                                <SelectItem value="contains">contiene</SelectItem>
                                                <SelectItem value="gt">&gt;</SelectItem>
                                                <SelectItem value="lt">&lt;</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            className="w-24 h-8 text-xs"
                                            value={filtro.valor}
                                            onChange={(e) => updateFiltro(idx, 'valor', e.target.value)}
                                            placeholder="Valor"
                                        />
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeFiltro(idx)}>
                                            <Trash2 className="h-3 w-3 text-red-500" />
                                        </Button>
                                    </div>
                                ))}
                                {filtros.length === 0 && (
                                    <p className="text-xs text-muted-foreground text-center py-2">Sin filtros</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Cálculos */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Calculator className="h-4 w-4" />
                                    Cálculos
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={addCalculo}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {calculos.map((calc, idx) => (
                                    <div key={idx} className="space-y-2 p-2 bg-muted/50 rounded">
                                        <div className="flex gap-2">
                                            <Input
                                                className="h-8 text-xs"
                                                value={calc.nombre}
                                                onChange={(e) => updateCalculo(idx, 'nombre', e.target.value)}
                                                placeholder="Nombre"
                                            />
                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeCalculo(idx)}>
                                                <Trash2 className="h-3 w-3 text-red-500" />
                                            </Button>
                                        </div>
                                        <div className="flex gap-2">
                                            <Select 
                                                value={calc.tipo} 
                                                onValueChange={(v) => updateCalculo(idx, 'tipo', v)}
                                            >
                                                <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="suma">Suma</SelectItem>
                                                    <SelectItem value="promedio">Promedio</SelectItem>
                                                    <SelectItem value="conteo">Conteo</SelectItem>
                                                    <SelectItem value="min">Mínimo</SelectItem>
                                                    <SelectItem value="max">Máximo</SelectItem>
                                                    <SelectItem value="formula">Fórmula</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {calc.tipo !== 'formula' && calc.tipo !== 'conteo' && (
                                                <Select 
                                                    value={calc.campo} 
                                                    onValueChange={(v) => updateCalculo(idx, 'campo', v)}
                                                >
                                                    <SelectTrigger className="h-8 text-xs">
                                                        <SelectValue placeholder="Campo" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {campos.filter(c => c.tipo === 'number' || c.tipo === 'currency').map(c => (
                                                            <SelectItem key={c.field} value={c.field}>{c.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                        {calc.tipo === 'formula' && (
                                            <Input
                                                className="h-8 text-xs"
                                                value={calc.formula}
                                                onChange={(e) => updateCalculo(idx, 'formula', e.target.value)}
                                                placeholder="Ej: monto * 0.19"
                                            />
                                        )}
                                    </div>
                                ))}
                                {calculos.length === 0 && (
                                    <p className="text-xs text-muted-foreground text-center py-2">Sin cálculos</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ordenamiento */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <ArrowUpDown className="h-4 w-4" />
                                Ordenamiento
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2">
                                <Select value={ordenarPor || "none"} onValueChange={(v) => setOrdenarPor(v === "none" ? "" : v)}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Ordenar por..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin ordenar</SelectItem>
                                        {columnasSeleccionadas.filter(c => c.visible).map(c => (
                                            <SelectItem key={c.field} value={c.field}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={orden} onValueChange={setOrden}>
                                    <SelectTrigger className="w-24">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="asc">A-Z</SelectItem>
                                        <SelectItem value="desc">Z-A</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Plantillas Guardadas */}
                    {plantillas.length > 0 && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Plantillas Guardadas</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {plantillas.map(p => (
                                        <Button
                                            key={p.id}
                                            variant="ghost"
                                            className="w-full justify-start text-sm"
                                            onClick={() => cargarPlantilla(p)}
                                        >
                                            <FileText className="h-4 w-4 mr-2" />
                                            {p.nombre}
                                        </Button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Panel de Resultado */}
                <div className="md:col-span-2">
                    <Card className="h-full">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Vista Previa</CardTitle>
                                    <CardDescription>
                                        {resultado ? `${resultado.total_registros} registros encontrados` : 'Genera el reporte para ver los datos'}
                                    </CardDescription>
                                </div>
                                {previewData.length > 0 && (
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => exportarReporte('csv')}
                                            disabled={exporting}
                                            data-testid="export-csv-btn"
                                        >
                                            CSV
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => exportarReporte('excel')}
                                            disabled={exporting}
                                            data-testid="export-excel-btn"
                                        >
                                            Excel
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => exportarReporte('pdf')}
                                            disabled={exporting}
                                            data-testid="export-pdf-btn"
                                        >
                                            PDF
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => exportarReporte('html')}
                                            disabled={exporting}
                                        >
                                            HTML
                                        </Button>
                                        {fuenteActual === 'dispositivos' || fuenteActual === 'vehiculos' ? (
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => exportarReporte('kmz')}
                                                disabled={exporting}
                                                data-testid="export-kmz-btn"
                                            >
                                                <Globe className="h-4 w-4 mr-1" />
                                                KMZ
                                            </Button>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {generating ? (
                                <div className="flex items-center justify-center h-64">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                </div>
                            ) : previewData.length > 0 ? (
                                <>
                                    <div className="border rounded-lg overflow-auto max-h-[500px]">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    {resultado?.columnas?.map(col => (
                                                        <TableHead key={col.field} className="whitespace-nowrap">
                                                            {col.label}
                                                        </TableHead>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {previewData.slice(0, 50).map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        {resultado?.columnas?.map(col => (
                                                            <TableCell key={col.field} className="whitespace-nowrap">
                                                                {row[col.field] || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                    {previewData.length > 50 && (
                                        <p className="text-sm text-muted-foreground text-center mt-4">
                                            Mostrando 50 de {previewData.length} registros. Exporta para ver todos.
                                        </p>
                                    )}
                                    
                                    {/* Resumen de cálculos */}
                                    {resultado?.resumen && Object.keys(resultado.resumen).length > 0 && (
                                        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                                            <h4 className="font-semibold mb-2">Resumen</h4>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {Object.entries(resultado.resumen).map(([key, value]) => (
                                                    <div key={key}>
                                                        <p className="text-sm text-muted-foreground">{key}</p>
                                                        <p className="font-bold">{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                                    <FileSpreadsheet className="h-12 w-12 mb-4" />
                                    <p>Configura tu reporte y presiona "Vista Previa"</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Dialog para guardar plantilla */}
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Guardar Plantilla</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nombre de la Plantilla</Label>
                            <Input
                                value={nombrePlantilla}
                                onChange={(e) => setNombrePlantilla(e.target.value)}
                                placeholder="Mi plantilla de reporte"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={guardarPlantilla}>
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
